/**
 * Custom Cart Drawer JavaScript
 * Handles drawer open/close, quantity updates, remove items, upsell add to cart, and timer
 */

class CustomCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('#CustomCartDrawer-Overlay');
    this.timerInterval = null;
    // Read timer duration from data attribute (in minutes), default to 10
    const timerMinutes = parseInt(this.dataset.timerDuration) || 10;
    this.timerDuration = timerMinutes * 60; // Convert to seconds
    this.confettiInstance = null;

    this.bindEvents();
    this.setupCartAddListener();
  }

  bindEvents() {
    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Close on escape key
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    });

    // Quantity buttons
    this.querySelectorAll('.custom-cart-drawer__qty-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e));
    });

    // Remove buttons
    this.querySelectorAll('.custom-cart-drawer__remove-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleRemoveItem(e));
    });

    // Upsell add to cart buttons
    this.querySelectorAll('.custom-cart-drawer__upsell-add').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleUpsellAdd(e));
    });

    // Upsell variant selectors
    this.querySelectorAll('.custom-cart-drawer__upsell-select').forEach(select => {
      select.addEventListener('change', (e) => this.handleVariantChange(e));
    });
  }

  /**
   * Setup listener for cart add events from ANY source
   * This intercepts all cart additions and updates the drawer
   */
  setupCartAddListener() {
    const drawer = this;

    // Intercept ALL fetch requests to /cart/add.js
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);

      // Check if this was a cart add request
      const url = args[0]?.toString() || args[0]?.url || '';
      if (url.includes('/cart/add')) {
        // Clone response to read it without consuming
        const clonedResponse = response.clone();
        try {
          const data = await clonedResponse.json();
          if (data && !data.errors) {
            // Delay slightly to ensure cart is updated on server
            setTimeout(async () => {
              await drawer.refreshDrawer();
              await drawer.updateCartCount();
            }, 100);
          }
        } catch (e) {
          // Response wasn't JSON, ignore
        }
      }

      return response;
    };

    // Listen for theme's cart events (multiple event names for compatibility)
    const cartEvents = ['cart:update', 'cart:add', 'cart:change', 'ajaxProduct:added'];
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, async (e) => {
        await this.refreshDrawer();
        await this.updateCartCount();
      });
    });

    // Listen for cart:updated event (from our own updates)
    document.addEventListener('cart:updated', async () => {
      await this.refreshDrawer();
      await this.updateCartCount();
    });

    // Also listen for Shopify's native section rendering events
    document.addEventListener('shopify:section:load', async () => {
      await this.updateCartCount();
    });
  }

  /**
   * Handle cart add event - refresh drawer and optionally open it
   */
  async onCartAdd(event) {
    // Refresh the drawer content
    await this.refreshDrawer();

    // Update the cart count in header
    await this.updateCartCount();

    // Auto-open the drawer when item is added
    // Check if auto_open setting is enabled (we default to opening)
    const shouldAutoOpen = true; // You can make this conditional based on settings
    if (shouldAutoOpen) {
      this.open();
    }
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      const cart = await response.json();

      // Update custom header cart count
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(el => {
        if (cart.item_count > 0) {
          el.textContent = cart.item_count;
          el.classList.add('has-items');
          // Add animation class
          el.classList.add('cart-count-updated');
          setTimeout(() => el.classList.remove('cart-count-updated'), 300);
        } else {
          el.textContent = '';
          el.classList.remove('has-items');
        }
      });

      // Also update theme cart bubble if exists
      const bubbleElements = document.querySelectorAll('.cart-bubble__text-count');
      bubbleElements.forEach(el => {
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
      });

      return cart;
    } catch (error) {
      console.error('Error updating cart count:', error);
      return null;
    }
  }

  async open() {
    // Always refresh drawer content when opening to ensure latest cart data
    await this.refreshDrawer();

    this.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.startTimer();

    // Check and fire confetti for free shipping
    this.checkAndFireConfetti();

    // Focus trap
    const firstFocusable = this.querySelector('button, [href], input, select, textarea');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
  }

  /**
   * Check if free shipping is unlocked and fire confetti
   */
  checkAndFireConfetti() {
    const freeShippingUnlocked = this.dataset.freeShippingUnlocked === 'true';
    const confettiFired = sessionStorage.getItem('cartConfettiFired');

    if (freeShippingUnlocked && !confettiFired) {
      // Small delay to let drawer animation complete
      setTimeout(() => {
        this.fireConfetti();
        sessionStorage.setItem('cartConfettiFired', 'true');
      }, 300);
    }
  }

  /**
   * Fire confetti celebration animation
   */
  fireConfetti() {
    const canvas = document.getElementById('confetti-canvas');
    if (!canvas || typeof confetti === 'undefined') {
      return;
    }

    // Create confetti instance
    const myConfetti = confetti.create(canvas, { resize: true, useWorker: true });
    const duration = 3 * 1000; // 3 seconds
    const end = Date.now() + duration;

    // Animate confetti falling from top
    (function frame() {
      myConfetti({
        particleCount: 5,
        spread: 70,
        angle: 270,
        origin: { y: -0.5 }
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  }

  close() {
    this.classList.remove('active');
    document.body.style.overflow = '';
    this.stopTimer();
  }

  startTimer() {
    const timerElement = this.querySelector('#cart-countdown');
    if (!timerElement) return;

    // Check if we have a saved timer
    let savedTime = sessionStorage.getItem('cartTimerEnd');
    let endTime;

    if (savedTime) {
      endTime = parseInt(savedTime);
      // If timer has expired, reset it
      if (endTime <= Date.now()) {
        endTime = Date.now() + (this.timerDuration * 1000);
        sessionStorage.setItem('cartTimerEnd', endTime);
      }
    } else {
      endTime = Date.now() + (this.timerDuration * 1000);
      sessionStorage.setItem('cartTimerEnd', endTime);
    }

    this.updateTimerDisplay(timerElement, endTime);

    this.timerInterval = setInterval(() => {
      this.updateTimerDisplay(timerElement, endTime);
    }, 1000);
  }

  updateTimerDisplay(element, endTime) {
    const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;

    element.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    if (remaining <= 0) {
      this.stopTimer();
      // Reset timer
      sessionStorage.removeItem('cartTimerEnd');
    }
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  async handleQuantityChange(e) {
    const button = e.currentTarget;
    const action = button.dataset.action;
    const index = button.dataset.index;
    const item = button.closest('.custom-cart-drawer__item');
    const input = item.querySelector('.custom-cart-drawer__qty-input');
    let quantity = parseInt(input.value);

    if (action === 'increase') {
      quantity++;
    } else if (action === 'decrease' && quantity > 1) {
      quantity--;
    } else if (action === 'decrease' && quantity === 1) {
      // Remove item if quantity goes to 0
      return this.handleRemoveItem({ currentTarget: item.querySelector('.custom-cart-drawer__remove-btn') });
    }

    item.classList.add('loading');
    await this.updateCartQuantity(index, quantity);
    item.classList.remove('loading');
  }

  async handleRemoveItem(e) {
    const button = e.currentTarget;
    const index = button.dataset.index;
    const item = button.closest('.custom-cart-drawer__item');

    item.classList.add('loading');
    await this.updateCartQuantity(index, 0);
  }

  async updateCartQuantity(index, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(index),
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();

      // Dispatch event for cart update
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      // Refresh the drawer
      await this.refreshDrawer();

      // Update cart count
      await this.updateCartCount();

    } catch (error) {
      console.error('Error updating cart:', error);
      // Remove loading state on error
      this.querySelectorAll('.custom-cart-drawer__item.loading').forEach(item => {
        item.classList.remove('loading');
      });
    }
  }

  async handleUpsellAdd(e) {
    const button = e.currentTarget;
    const productId = button.dataset.productId;
    let variantId = button.dataset.variantId;
    const item = button.closest('.custom-cart-drawer__upsell-item');

    // Get selected variant from dropdowns if any
    const selects = item.querySelectorAll('.custom-cart-drawer__upsell-select');
    if (selects.length > 0) {
      // We need to find the correct variant based on selected options
      const handle = item.dataset.productHandle;
      try {
        const productResponse = await fetch(`/products/${handle}.js`);
        if (!productResponse.ok) throw new Error('Failed to fetch product');
        const product = await productResponse.json();

        const selectedOptions = [];
        selects.forEach(select => {
          selectedOptions.push(select.value);
        });

        // Find matching variant
        const matchingVariant = product.variants.find(variant => {
          return selectedOptions.every((option, index) => {
            return variant.options[index] === option;
          });
        });

        if (matchingVariant) {
          variantId = matchingVariant.id;
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }
    }

    button.classList.add('loading');
    button.textContent = 'ADDING...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      // Dispatch event for cart update
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Refresh the drawer
      await this.refreshDrawer();

      // Update cart count
      await this.updateCartCount();

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.classList.remove('loading');
      button.textContent = 'ADD TO CART';
    }
  }

  async handleVariantChange(e) {
    const select = e.currentTarget;
    const productId = select.dataset.productId;
    const item = select.closest('.custom-cart-drawer__upsell-item');
    const handle = item.dataset.productHandle;
    const priceElement = this.querySelector(`#upsell-price-${productId}`);

    try {
      const productResponse = await fetch(`/products/${handle}.js`);
      if (!productResponse.ok) throw new Error('Failed to fetch product');
      const product = await productResponse.json();

      const selects = item.querySelectorAll('.custom-cart-drawer__upsell-select');
      const selectedOptions = [];
      selects.forEach(sel => {
        selectedOptions.push(sel.value);
      });

      // Find matching variant
      const matchingVariant = product.variants.find(variant => {
        return selectedOptions.every((option, index) => {
          return variant.options[index] === option;
        });
      });

      if (matchingVariant && priceElement) {
        // Update price display
        const formattedPrice = this.formatMoney(matchingVariant.price);
        priceElement.textContent = formattedPrice;

        // Update variant ID on add button
        const addButton = item.querySelector('.custom-cart-drawer__upsell-add');
        if (addButton) {
          addButton.dataset.variantId = matchingVariant.id;
        }
      }
    } catch (error) {
      console.error('Error fetching variant:', error);
    }
  }

  formatMoney(cents) {
    // Format as Indian Rupees
    return 'Rs. ' + (cents / 100).toFixed(2);
  }

  /**
   * Calculate unlocked discount based on cart total and milestone settings
   */
  calculateUnlockedDiscount(cartTotal) {
    let unlockedDiscount = 0;

    // Note: data-milestone-1-enabled becomes dataset['milestone-1Enabled'] (hyphen preserved before number)
    const milestone1Enabled = this.dataset['milestone-1Enabled'] === 'true';
    const milestone1Amount = parseFloat(this.dataset['milestone-1Amount']) || 0;
    const milestone1Discount = parseFloat(this.dataset['milestone-1Discount']) || 0;

    const milestone2Enabled = this.dataset['milestone-2Enabled'] === 'true';
    const milestone2Amount = parseFloat(this.dataset['milestone-2Amount']) || 0;
    const milestone2Discount = parseFloat(this.dataset['milestone-2Discount']) || 0;

    const milestone3Enabled = this.dataset['milestone-3Enabled'] === 'true';
    const milestone3Amount = parseFloat(this.dataset['milestone-3Amount']) || 0;
    const milestone3Discount = parseFloat(this.dataset['milestone-3Discount']) || 0;

    // Check each milestone and get the highest unlocked discount
    if (milestone1Enabled && cartTotal >= milestone1Amount && milestone1Discount > unlockedDiscount) {
      unlockedDiscount = milestone1Discount;
    }
    if (milestone2Enabled && cartTotal >= milestone2Amount && milestone2Discount > unlockedDiscount) {
      unlockedDiscount = milestone2Discount;
    }
    if (milestone3Enabled && cartTotal >= milestone3Amount && milestone3Discount > unlockedDiscount) {
      unlockedDiscount = milestone3Discount;
    }

    return unlockedDiscount;
  }

  /**
   * Update price breakdown in footer
   */
  updatePriceBreakdown(cartTotal) {
    const unlockedDiscount = this.calculateUnlockedDiscount(cartTotal);
    const discountAmount = (cartTotal * unlockedDiscount) / 100;
    const finalTotal = cartTotal - discountAmount;

    // Update Total Amount
    const totalElement = this.querySelector('#CustomCartDrawer-Total');
    if (totalElement) {
      totalElement.textContent = 'Rs. ' + cartTotal.toFixed(2);
    }

    // Update Discount Row
    const discountRow = this.querySelector('#CustomCartDrawer-DiscountRow');
    const discountPercentage = this.querySelector('#discount-percentage');
    const discountValue = this.querySelector('#CustomCartDrawer-DiscountValue');

    if (discountRow && discountPercentage && discountValue) {
      if (unlockedDiscount > 0) {
        discountRow.style.display = 'flex';
        discountPercentage.textContent = unlockedDiscount;
        discountValue.textContent = '-Rs. ' + discountAmount.toFixed(2);
      } else {
        discountRow.style.display = 'none';
      }
    }

    // Update Final Total
    const finalTotalElement = this.querySelector('#CustomCartDrawer-FinalTotal');
    if (finalTotalElement) {
      finalTotalElement.textContent = 'Rs. ' + finalTotal.toFixed(2);
    }

    // Update data attribute for reference
    const priceBreakdown = this.querySelector('#CustomCartDrawer-PriceBreakdown');
    if (priceBreakdown) {
      priceBreakdown.dataset.unlockedDiscount = unlockedDiscount;
    }
  }

  async refreshDrawer() {
    try {
      const timestamp = Date.now();
      const response = await fetch(`/?sections=custom-cart-drawer&t=${timestamp}`);
      if (!response.ok) throw new Error('Failed to fetch cart drawer sections');
      const sections = await response.json();

      const sectionHtml = sections['custom-cart-drawer'];
      if (sectionHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const newDrawer = doc.querySelector('custom-cart-drawer');

        if (newDrawer) {
          // Check if cart is now empty
          if (newDrawer.classList.contains('is-empty')) {
            this.classList.add('is-empty');
          } else {
            this.classList.remove('is-empty');
          }

          // Update data attributes for free shipping state
          const wasUnlocked = this.dataset.freeShippingUnlocked === 'true';
          const nowUnlocked = newDrawer.dataset.freeShippingUnlocked === 'true';
          this.dataset.freeShippingUnlocked = newDrawer.dataset.freeShippingUnlocked;
          this.dataset.threshold = newDrawer.dataset.threshold;
          this.dataset.cartTotal = newDrawer.dataset.cartTotal;

          // Update milestone data attributes (hyphen preserved before numbers in dataset)
          this.dataset['milestone-1Enabled'] = newDrawer.dataset['milestone-1Enabled'];
          this.dataset['milestone-1Amount'] = newDrawer.dataset['milestone-1Amount'];
          this.dataset['milestone-1Discount'] = newDrawer.dataset['milestone-1Discount'];
          this.dataset['milestone-2Enabled'] = newDrawer.dataset['milestone-2Enabled'];
          this.dataset['milestone-2Amount'] = newDrawer.dataset['milestone-2Amount'];
          this.dataset['milestone-2Discount'] = newDrawer.dataset['milestone-2Discount'];
          this.dataset['milestone-3Enabled'] = newDrawer.dataset['milestone-3Enabled'];
          this.dataset['milestone-3Amount'] = newDrawer.dataset['milestone-3Amount'];
          this.dataset['milestone-3Discount'] = newDrawer.dataset['milestone-3Discount'];
          this.dataset.activeMilestones = newDrawer.dataset.activeMilestones;

          // Update inner content
          const newInner = newDrawer.querySelector('.custom-cart-drawer__inner');
          const currentInner = this.querySelector('.custom-cart-drawer__inner');

          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
            this.bindEvents(); // Rebind events to new elements

            // Update price breakdown with new cart total
            const cartTotal = parseFloat(this.dataset.cartTotal) || 0;
            this.updatePriceBreakdown(cartTotal);
          }

          // Fire confetti if free shipping was just unlocked
          if (nowUnlocked && !wasUnlocked) {
            this.fireConfetti();
            sessionStorage.setItem('cartConfettiFired', 'true');
          }

          // Reset confetti flag if dropped below threshold (so it can fire again)
          if (!nowUnlocked && wasUnlocked) {
            sessionStorage.removeItem('cartConfettiFired');
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing drawer:', error);
      // Don't reload - just log the error and continue
    }
  }
}

// Define custom element
if (!customElements.get('custom-cart-drawer')) {
  customElements.define('custom-cart-drawer', CustomCartDrawer);
}

// Global function to open cart drawer (called from header)
window.openCustomCartDrawer = function() {
  const drawer = document.querySelector('custom-cart-drawer');
  if (drawer) {
    drawer.open();
  }
};

// Listen for cart:updated event (from our own internal updates)
document.addEventListener('cart:updated', async function() {
  const drawer = document.querySelector('custom-cart-drawer');
  if (drawer) {
    await drawer.updateCartCount();
  }
});
